// $RE₿EL Tokens page — /shadow/tokens
// Token: RE₿EL · Symbol: R₿L · Platform: RE₿EL Academy · Network: Base L2
// Contract: 0xf9214b42c4c53791846E90066E52D7306adFACf5
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Coins, Crown, ShoppingBag, Sparkles, Loader2, Check, AlertCircle,
  History, ExternalLink, TrendingUp, CheckCircle2, Circle, BarChart3, FileText,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { OnchainConvertCard } from "../components/shadow/OnchainConvertCard";
import SwapWidget from "../components/token/SwapWidget";
import BuyGuide from "../components/token/BuyGuide";
import TokenomicsDashboard from "../components/token/TokenomicsDashboard";
import SecurityAuditBadge from "../components/token/SecurityAuditBadge";
import BurnStats from "../components/burn/BurnStats";
import { useLanguage } from "../i18n/LanguageContext";
import { tFases } from "../i18n/shadow-fases";

const CONTRACT = "0xf9214b42c4c53791846E90066E52D7306adFACf5";
const BASESCAN_TOKEN = `https://basescan.org/address/${CONTRACT}`;
const UNISWAP_URL = `https://app.uniswap.org/swap?chain=base&inputCurrency=ETH&outputCurrency=${CONTRACT}`;
const PANCAKE_URL = `https://pancakeswap.finance/swap?chain=base&outputCurrency=${CONTRACT}`;
const DEXSCREENER_URL = `https://dexscreener.com/base/${CONTRACT}`;

const ICONS = { coupon: ShoppingBag, boost: Sparkles, badge: Crown };

export default function ShadowTokensPage() {
  const { user, token, openLogin } = useAuth();
  const { lang } = useLanguage();
  const [balance, setBalance] = useState(0);
  const [lifetime, setLifetime] = useState({});
  const [history, setHistory] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [lastRedemption, setLastRedemption] = useState(null);
  const [stats, setStats] = useState(null); // { price_usd, market_cap, liquidity, etc. }
  // Pair (pool) address resolved at runtime — used by the DexScreener iframe
  // so the chart axis shows USD instead of the WETH-denominated default.
  const [pairAddress, setPairAddress] = useState(null);
  const swapRef = useRef(null);

  const REASON_LABELS = {
    publish_analysis: tFases(lang, "t_r_publish"),
    receive_reaction: tFases(lang, "t_r_reaction"),
    direction_correct: tFases(lang, "t_r_direction"),
    referral: tFases(lang, "t_r_referral"),
  };

  const DEFAULT_CATALOG_I18N = [
    { id: "store_discount_5eur", name: tFases(lang, "tk_def_coupon_name"), description: tFases(lang, "tk_def_coupon_desc"), cost: 100, type: "coupon" },
    { id: "feed_boost_24h",      name: tFases(lang, "tk_def_boost_name"),  description: tFases(lang, "tk_def_boost_desc"),  cost: 50,  type: "boost" },
    { id: "badge_veteran",       name: tFases(lang, "tk_def_badge_name"),  description: tFases(lang, "tk_def_badge_desc"),  cost: 1000, type: "badge" },
  ];

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadAll = useCallback(async () => {
    const promises = [
      fetch(`${API}/shadow/tokens/catalog`).then(safeJson),
    ];
    if (user) {
      promises.push(
        fetch(`${API}/shadow/tokens/balance`, { credentials: "include", headers }).then(safeJson),
        fetch(`${API}/shadow/tokens/history`, { credentials: "include", headers }).then(safeJson),
      );
    }
    const [c, b, h] = await Promise.all(promises);
    if (c?.items) setCatalog(c.items);
    if (b) { setBalance(b.balance || 0); setLifetime(b.lifetime_earned || {}); }
    if (h?.events) setHistory(h.events);
  }, [user, token, headers]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live R₿L stats — polled every 30s.
  // Primary: GeckoTerminal (indexes the main Uniswap V3 pool with the real
  // liquidity). Fallback: DexScreener (only tracks a minor PancakeSwap pool).
  useEffect(() => {
    let mounted = true;

    const fromGecko = async () => {
      try {
        const r = await fetch(`https://api.geckoterminal.com/api/v2/networks/base/tokens/${CONTRACT}/pools`);
        const d = await r.json();
        const pools = d?.data || [];
        // Pool with highest reserve_in_usd wins.
        pools.sort(
          (a, b) =>
            parseFloat(b?.attributes?.reserve_in_usd || 0) -
            parseFloat(a?.attributes?.reserve_in_usd || 0)
        );
        const top = pools[0];
        if (!top) return null;
        const a = top.attributes || {};
        if (a.address && mounted) setPairAddress(a.address.toLowerCase());
        return {
          price_usd: parseFloat(a.base_token_price_usd || 0),
          change_24h: parseFloat(a?.price_change_percentage?.h24 || 0),
          liquidity_usd: parseFloat(a.reserve_in_usd || 0),
          volume_24h: parseFloat(a?.volume_usd?.h24 || 0),
          market_cap: parseFloat(a.market_cap_usd || a.fdv_usd || 0),
          dex: (top?.relationships?.dex?.data?.id || "uniswap").replace(/-base$/, "").replace(/-/g, " "),
          pair_url: `https://www.geckoterminal.com/base/pools/${(a.address || "").toLowerCase()}`,
        };
      } catch (_e) { return null; }
    };

    const fetchStats = async () => {
      // GeckoTerminal goes FIRST: it indexes the main Uniswap V3 pool
      // (highest liquidity), while DexScreener only tracks a minor
      // PancakeSwap pool with almost no trades — which made the embedded
      // chart render empty. Gecko resolves the correct pairAddress.
      const g = await fromGecko();
      if (mounted && g) { setStats(g); return; }

      // Fallback: DexScreener
      try {
        const r = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${CONTRACT}`);
        const d = await r.json();
        const pair = (d?.pairs || []).sort(
          (a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0)
        )[0];
        if (pair && parseFloat(pair.priceUsd || 0) > 0) {
          if (pair.pairAddress && mounted) setPairAddress(pair.pairAddress.toLowerCase());
          if (mounted) setStats({
            price_usd: parseFloat(pair.priceUsd || 0),
            change_24h: parseFloat(pair.priceChange?.h24 || 0),
            liquidity_usd: parseFloat(pair.liquidity?.usd || 0),
            volume_24h: parseFloat(pair.volume?.h24 || 0),
            market_cap: parseFloat(pair.marketCap || pair.fdv || 0),
            dex: pair.dexId,
            pair_url: pair.url,
          });
          return;
        }
      } catch (_e) {
        console.warn("[ShadowTokens] DexScreener fallback failed:", _e?.message || _e);
      }
    };

    fetchStats();
    const t = setInterval(fetchStats, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const redeem = async (itemId) => {
    if (busy) return;
    setBusy(itemId); setErr(null); setLastRedemption(null);
    try {
      const r = await fetch(`${API}/shadow/tokens/spend`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ item_id: itemId }),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d?.detail?.message || d?.detail?.error || d?.detail || "Error canjeando");
      setLastRedemption(d.redemption);
      await loadAll();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const lifeTotal = Object.values(lifetime).reduce((a, b) => a + Number(b || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="tokens-page">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/shadow" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 hover:text-amber-300" data-testid="tokens-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {tFases(lang, "common_back")}
        </Link>

        {/* HERO */}
        <div className="mt-8 flex flex-col items-center gap-4 text-center md:flex-row md:items-center md:gap-6 md:text-left">
          <div
            data-testid="tokens-hero-logo"
            className="h-[120px] w-[120px] shrink-0 overflow-hidden rounded-full shadow-[0_8px_40px_rgba(168,85,247,0.55)] ring-2 ring-violet-400/40"
          >
            <img
              src="https://res.cloudinary.com/dubfcngsr/image/upload/v1780856140/rebel/rebel_token_logo_circular.png"
              alt="RE₿EL Token"
              className="h-full w-full object-cover"
              loading="eager"
              decoding="async"
            />
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">
              {tFases(lang, "tk_hero_eyebrow")}
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-6xl">
              $RE₿EL · <span className="text-amber-300">R₿L</span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-white/55">
              {tFases(lang, "tk_hero_sub")}
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 md:justify-start">
              <a
                href={BASESCAN_TOKEN} target="_blank" rel="noreferrer"
                data-testid="tokens-basescan-link"
                className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] text-white/70 hover:border-amber-400/40 hover:text-amber-300"
              >
                {CONTRACT.slice(0, 10)}…{CONTRACT.slice(-6)} <ExternalLink className="h-3 w-3" />
              </a>
              <a
                href={DEXSCREENER_URL} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] text-white/70 hover:border-cyan-400/40 hover:text-cyan-300"
              >
                DexScreener <ExternalLink className="h-3 w-3" />
              </a>
              <Link
                to="/whitepaper"
                data-testid="tokens-whitepaper-btn"
                className="group relative inline-flex items-center gap-1.5 rounded-full border border-amber-300/60 bg-black px-4 py-1.5 font-mono text-[10px] font-black uppercase tracking-[0.22em] text-amber-200 transition-all hover:-translate-y-0.5"
                style={{ boxShadow: "0 0 18px rgba(0,229,255,0.35), 0 0 28px rgba(168,85,247,0.30)" }}
              >
                <FileText className="h-3 w-3" />
                Whitepaper
              </Link>
            </div>
          </div>
        </div>

        {/* LIVE STATS — DexScreener real-time (refresh every 30s) */}
        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="tokens-stats">
          <StatPill label={tFases(lang, "tk_stat_price")}
            value={stats?.price_usd ? `$${stats.price_usd.toFixed(stats.price_usd < 0.01 ? 8 : 4)}` : "—"}
            extra="USD"
          />
          <StatPill label="24h"
            value={stats?.change_24h != null ? `${stats.change_24h >= 0 ? "+" : ""}${stats.change_24h.toFixed(2)}%` : "—"}
            extra={stats?.change_24h != null ? (stats.change_24h >= 0 ? "▲ up" : "▼ down") : null}
            extraColor={stats?.change_24h >= 0 ? "text-emerald-300" : "text-rose-300"}
          />
          <StatPill label={tFases(lang, "tk_stat_liq")}
            value={stats?.liquidity_usd ? `$${Math.round(stats.liquidity_usd).toLocaleString()}` : "—"}
            extra={stats?.dex ? stats.dex.toUpperCase() : null}
          />
          <StatPill label="Market Cap"
            value={stats?.market_cap ? `$${Math.round(stats.market_cap).toLocaleString()}` : "—"}
            extra="FDV"
          />
        </div>

        {/* LIQUIDEZ BLOQUEADA — verificable on-chain en PinkLock */}
        {(() => {
          const LL_TXT = {
            es: { title: "Liquidez Bloqueada", locked_until: "Bloqueada hasta", month_of: "de junio de", months: "12 meses", verified: "Verificado en PinkLock", view_proof: "Ver prueba del lock" },
            en: { title: "Locked Liquidity", locked_until: "Locked until", month_of: "June", months: "12 months", verified: "Verified on PinkLock", view_proof: "View lock proof" },
            sk: { title: "Uzamknutá likvidita", locked_until: "Uzamknuté do", month_of: "júna", months: "12 mesiacov", verified: "Overené na PinkLock", view_proof: "Zobraziť dôkaz" },
            de: { title: "Gesperrte Liquidität", locked_until: "Gesperrt bis", month_of: "Juni", months: "12 Monate", verified: "Auf PinkLock verifiziert", view_proof: "Sperrnachweis ansehen" },
            "zh-CN": { title: "已锁定流动性", locked_until: "锁定至", month_of: "6月", months: "12 个月", verified: "在 PinkLock 上已验证", view_proof: "查看锁仓证明" },
          };
          const ll = LL_TXT[lang] || LL_TXT.en || LL_TXT.es;
          const dateStr = lang === "es" ? `9 ${ll.month_of} 2027` : lang === "sk" ? `9. ${ll.month_of} 2027` : lang === "de" ? `9. ${ll.month_of} 2027` : lang === "zh-CN" ? `2027年${ll.month_of}9日` : `${ll.month_of} 9, 2027`;
          return (
        <div
          className="mt-6 overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-500/[0.06] to-emerald-500/[0.02] p-5 backdrop-blur"
          style={{ borderColor: "rgba(99,153,34,0.45)", boxShadow: "0 0 28px rgba(99,153,34,0.18)" }}
          data-testid="tokens-liquidity-lock"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="mb-1 inline-flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-[0.25em]" style={{ color: "#9bc864" }}>
                <span>🔒</span>
                <span>{ll.title}</span>
              </div>
              <div className="text-sm font-bold text-white">
                PancakeSwap Base · 1,168 Cake-LP · $152 USD
              </div>
              <div className="mt-1 text-xs text-white/65">
                {ll.locked_until}: <span className="font-bold text-white/85">{dateStr}</span> <span className="text-white/45">({ll.months})</span>
              </div>
              <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#9bc864" }}>
                <span>✅</span>
                <span>{ll.verified}</span>
              </div>
            </div>
            <a
              href="https://www.pinksale.finance/pinklock/base/record/1044713"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tokens-liquidity-lock-link"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border px-4 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-white transition-all hover:-translate-y-0.5"
              style={{ borderColor: "rgba(99,153,34,0.55)", background: "rgba(99,153,34,0.10)", boxShadow: "0 0 18px rgba(99,153,34,0.25)" }}
            >
              {ll.view_proof} →
            </a>
          </div>
        </div>
          );
        })()}

        {/* TOKENOMICS DASHBOARD — real-time on-chain metrics (above the chart) */}
        <div className="mt-6 mb-6">
          <TokenomicsDashboard />
        </div>

        {/* BURN STATS — mechanical deflation (5% of each on-chain PRO payment) */}
        <div className="mb-6">
          <BurnStats />
        </div>

        {/* SECURITY AUDIT BADGE — ecosystem hardening report (additive) */}
        <div className="mb-6">
          <SecurityAuditBadge />
        </div>

        {/* LIVE CANDLE CHART — GeckoTerminal embed pointing at the main
            Uniswap V3 pool (resolved at runtime, highest liquidity).
            DexScreener is only used while the pool address resolves, since
            it doesn't index the main pool and rendered an empty chart. */}
        <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur" data-testid="tokens-dex-iframe-wrapper">
          <iframe
            title="R₿L Token Chart"
            src={pairAddress
              ? `https://www.geckoterminal.com/base/pools/${pairAddress}?embed=1&info=0&swaps=0&grayscale=0&light_chart=0&chart_type=price&resolution=1d`
              : `https://dexscreener.com/base/${CONTRACT}?embed=1&theme=dark&trades=0&info=0`}
            data-testid="tokens-dex-iframe"
            className="block w-full h-[350px] md:h-[500px] border-0"
            loading="lazy"
            allowFullScreen
          />
        </div>

        {/* BUY GUIDE — compact 4-step timeline pointing to the swap widget below */}
        <div className="mt-8">
          <BuyGuide onScrollToSwap={() => swapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })} />
        </div>

        {/* SWAP WIDGET — buy R₿L in-page (wagmi + Uniswap V3 on Base) */}
        <div className="max-w-md mx-auto mt-6" ref={swapRef}>
          <SwapWidget />
        </div>

        {/* SWAP CARD */}
        <div className="mt-6 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.06] to-orange-500/[0.03] p-5 backdrop-blur" data-testid="tokens-swap-card">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-amber-200">
            <TrendingUp className="h-4 w-4" /> {tFases(lang, "tk_swap_title")}
          </h2>
          <p className="mt-1 text-xs text-white/55">
            {tFases(lang, "tk_swap_sub")}
          </p>
          <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-4">
            <a
              href="https://thirdweb.com/base/0xf9214b42c4c53791846E90066E52D7306adFACf5"
              target="_blank" rel="noopener noreferrer" data-testid="tokens-swap-buy-rbl"
              className="group flex items-center justify-between gap-2 rounded-xl px-4 py-3 transition-all hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg,#f5a623,#e8941a)" }}
            >
              <span className="font-bold" style={{ color: "#07080f" }}>{({es: "COMPRAR R₿L →", en: "BUY R₿L →", sk: "KÚPIŤ R₿L →", de: "R₿L KAUFEN →", "zh-CN": "购买 R₿L →"})[lang] || "BUY R₿L →"}</span>
              <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5" style={{ color: "#07080f" }} />
            </a>
            <a href={UNISWAP_URL} target="_blank" rel="noreferrer" data-testid="tokens-swap-uniswap"
              className="group flex items-center justify-between gap-2 rounded-xl border border-pink-400/30 bg-pink-400/[0.05] px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-pink-400/60">
              <span className="font-bold text-pink-200">Uniswap (Base)</span>
              <ExternalLink className="h-4 w-4 text-pink-300 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a href={PANCAKE_URL} target="_blank" rel="noreferrer" data-testid="tokens-swap-pancake"
              className="group flex items-center justify-between gap-2 rounded-xl border border-amber-400/30 bg-amber-400/[0.05] px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-amber-400/60">
              <span className="font-bold text-amber-200">🥞 PancakeSwap (Base)</span>
              <ExternalLink className="h-4 w-4 text-amber-300 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a href={DEXSCREENER_URL} target="_blank" rel="noreferrer" data-testid="tokens-swap-dexscreener"
              className="group flex items-center justify-between gap-2 rounded-xl border border-violet-400/30 bg-violet-400/[0.05] px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-violet-400/60">
              <span className="font-bold text-violet-200">{tFases(lang, "tk_swap_chart")}</span>
              <ExternalLink className="h-4 w-4 text-violet-300 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>

        {/* BALANCE (only if logged in) */}
        {user && (
          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="tokens-balance-section">
            <div className="md:col-span-2 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/[0.08] to-orange-500/[0.04] p-6 backdrop-blur">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-200/80">{tFases(lang, "tk_balance_label")}</p>
              <p className="mt-1 font-mono text-6xl font-black text-amber-200" data-testid="tokens-balance">
                <Coins className="inline mb-2 mr-2 h-10 w-10 text-amber-300" />
                {balance.toLocaleString()}
              </p>
              <p className="mt-2 text-xs text-white/55">
                {tFases(lang, "tk_balance_total")} <span className="font-mono font-bold text-white/80">{lifeTotal.toLocaleString()}</span> R₿L
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 backdrop-blur">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">{tFases(lang, "tk_how_earn_title")}</p>
              <ul className="mt-2 space-y-1 text-[11px] text-white/65">
                <li>{tFases(lang, "t_e_publish")}</li>
                <li>{tFases(lang, "t_e_react")}</li>
                <li>{tFases(lang, "t_e_correct")}</li>
                <li>{tFases(lang, "t_e_referral")}</li>
              </ul>
              <Link
                to="/shadow/subscripciones-rbl"
                data-testid="tokens-rbl-subs-link"
                className="mt-4 inline-flex w-full items-center justify-between gap-2 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[10.5px] font-mono uppercase tracking-[0.14em] text-amber-200 transition-all hover:border-amber-400/60 hover:bg-amber-400/[0.12]"
              >
                <span>
                  {{
                    es: "Tus suscripciones R₿L",
                    en: "Your R₿L subscriptions",
                    sk: "Predplatné R₿L", de: "Deine R₿L-Abonnements", "zh-CN": "您的 R₿L 订阅",
                  }[lang] || "Tus suscripciones R₿L"}
                </span>
                <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        )}

        {!user && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.025] p-6 text-center backdrop-blur">
            <p className="text-sm text-white/55">{tFases(lang, "tk_login_sub")}</p>
            <button onClick={openLogin} data-testid="tokens-login-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black">
              {tFases(lang, "common_login")}
            </button>
          </div>
        )}

        {/* STAKING — virtual 30d lockup yielding ⚡ ×1.5 multiplier */}
        {user && <StakingSection balance={balance} onMutated={loadAll} lang={lang} token={token} />}

        {/* ON-CHAIN CONVERSION (existing component kept untouched) */}
        {user && <OnchainConvertCard virtualBalance={balance} onConverted={loadAll} />}

        {/* CANJEAR R₿L */}
        <div className="mt-8" data-testid="tokens-redeem-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-white/75">{tFases(lang, "tk_redeem_title")}</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            {(catalog.length ? catalog : DEFAULT_CATALOG_I18N).map((it) => {
              const Icon = ICONS[it.type] || Coins;
              const canAfford = balance >= it.cost;
              // i18n override by known item.id; falls back to backend-provided name.
              const i18nName = {
                store_discount_5eur: tFases(lang, "tk_def_coupon_name"),
                feed_boost_24h: tFases(lang, "tk_def_boost_name"),
                badge_veteran: tFases(lang, "tk_def_badge_name"),
              }[it.id];
              const i18nDesc = {
                store_discount_5eur: tFases(lang, "tk_def_coupon_desc"),
                feed_boost_24h: tFases(lang, "tk_def_boost_desc"),
                badge_veteran: tFases(lang, "tk_def_badge_desc"),
              }[it.id];
              return (
                <div key={it.id}
                  className={`relative overflow-hidden rounded-2xl border p-5 transition-all ${
                    canAfford ? "border-emerald-400/30 bg-emerald-400/[0.04]" : "border-white/10 bg-white/[0.025]"
                  }`}
                  data-testid={`tokens-item-${it.id}`}
                >
                  <Icon className={`h-6 w-6 ${canAfford ? "text-emerald-300" : "text-white/40"}`} />
                  <p className="mt-3 text-sm font-bold text-white">{i18nName || it.name}</p>
                  <p className="mt-1 text-[11px] text-white/55">{i18nDesc || it.description}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-mono text-base font-black text-amber-200">{it.cost} R₿L</span>
                    {user && (
                      <button onClick={() => redeem(it.id)} disabled={!canAfford || busy === it.id}
                        data-testid={`tokens-redeem-${it.id}`}
                        className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all ${
                          canAfford ? "bg-emerald-400 text-black hover:-translate-y-0.5" : "bg-white/10 text-white/30 cursor-not-allowed"
                        }`}
                      >
                        {busy === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : tFases(lang, "tk_redeem_btn")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {err && (
            <div className="mt-3 rounded-xl border border-rose-500/40 bg-rose-500/[0.08] px-3 py-2 text-xs text-rose-200" data-testid="tokens-err">
              <AlertCircle className="inline h-3.5 w-3.5 mr-1" /> {err}
            </div>
          )}
          {lastRedemption && (
            <div className="mt-3 rounded-2xl border border-emerald-400/40 bg-emerald-400/[0.06] p-4 backdrop-blur" data-testid="tokens-redemption-result">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-200/80">
                <Check className="inline mr-1 h-3 w-3" /> {tFases(lang, "tk_redeem_ok")}
              </p>
              <p className="mt-1 text-sm font-bold text-white">{lastRedemption.payload?.name}</p>
              {lastRedemption.payload?.code && (
                <p className="mt-2 font-mono text-lg font-black text-emerald-200">{lastRedemption.payload.code}</p>
              )}
              {lastRedemption.payload?.boost_until && (
                <p className="mt-1 text-xs text-white/55">{tFases(lang, "tk_redeem_active", { when: new Date(lastRedemption.payload.boost_until).toLocaleString() })}</p>
              )}
            </div>
          )}
        </div>

        {/* ROADMAP */}
        <div className="mt-12" data-testid="tokens-roadmap">
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-white/75">{tFases(lang, "tk_roadmap_title")}</h2>
          <div className="mt-4 space-y-3">
            <Phase status="done" icon="✅" title={tFases(lang, "tk_phase1_title")} items={[
              tFases(lang, "tk_phase1_i1"),
              tFases(lang, "tk_phase1_i2"),
              tFases(lang, "tk_phase1_i3"),
              tFases(lang, "tk_phase1_i4"),
              tFases(lang, "tk_phase1_i5"),
            ]} />
            <Phase status="done" icon="✅" title={tFases(lang, "tk_phase2_title")} items={[
              tFases(lang, "tk_phase2_i1"),
              tFases(lang, "tk_phase2_i2"),
              tFases(lang, "tk_phase2_i3"),
              tFases(lang, "tk_phase2_i4"),
              tFases(lang, "tk_phase2_i5"),
            ]} />
            <Phase status="done" icon="✅" title={tFases(lang, "tk_phase3_title")} items={[
              tFases(lang, "tk_phase3_i1"),
              tFases(lang, "tk_phase3_i2"),
              tFases(lang, "tk_phase3_i3"),
              tFases(lang, "tk_phase3_i4"),
              tFases(lang, "tk_phase3_i5"),
            ]} />
            <Phase status="done" icon="✅" title={tFases(lang, "tk_phase4_title")} items={[
              tFases(lang, "tk_phase4_i1"),
              tFases(lang, "tk_phase4_i2"),
              tFases(lang, "tk_phase4_i3"),
              tFases(lang, "tk_phase4_i4"),
              tFases(lang, "tk_phase4_i5"),
              tFases(lang, "tk_phase4_i6"),
            ]} />
            <Phase status="done" icon="✅" title={tFases(lang, "tk_phase5_title")} items={[
              tFases(lang, "tk_phase5_i1"),
              tFases(lang, "tk_phase5_i2"),
              tFases(lang, "tk_phase5_i3"),
              tFases(lang, "tk_phase5_i4"),
              tFases(lang, "tk_phase5_i5"),
            ]} />
            <Phase status="active" icon="🔄" title={tFases(lang, "tk_phase6_title")} items={[
              tFases(lang, "tk_phase6_i1"),
              tFases(lang, "tk_phase6_i2"),
              tFases(lang, "tk_phase6_i3"),
              tFases(lang, "tk_phase6_i4"),
              tFases(lang, "tk_phase6_i5"),
              tFases(lang, "tk_phase6_i6"),
              tFases(lang, "tk_phase6_i7"),
            ]} />
            <Phase status="upcoming" icon="⚪" title={tFases(lang, "tk_phase7_title")} items={[
              tFases(lang, "tk_phase7_i1"),
              tFases(lang, "tk_phase7_i2"),
              tFases(lang, "tk_phase7_i3"),
              tFases(lang, "tk_phase7_i4"),
              tFases(lang, "tk_phase7_i5"),
              tFases(lang, "tk_phase7_i6"),
              tFases(lang, "tk_phase7_i7"),
              tFases(lang, "tk_phase7_i8"),
            ]} />
          </div>
        </div>

        {/* HISTORY */}
        {user && (
          <div className="mt-8">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-white/75">
              <History className="h-4 w-4" /> {tFases(lang, "tk_history_title")} ({history.length})
            </h2>
            {history.length === 0 ? (
              <p className="mt-2 text-xs text-white/45">{tFases(lang, "tk_history_empty")}</p>
            ) : (
              <ul className="mt-3 divide-y divide-white/5 rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur">
                {history.map((ev) => (
                  <li key={ev.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`tokens-event-${ev.id}`}>
                    <span className="truncate text-[12px] text-white/75">{REASON_LABELS[ev.reason] || ev.reason}</span>
                    <span className={`font-mono text-sm font-black ${ev.amount > 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {ev.amount > 0 ? "+" : ""}{ev.amount} R₿L
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

// ─── Live R₿L Price Chart (lightweight-charts via CDN) ──────────────────────
// Self-contained, isolated component. Loads the lightweight-charts UMD bundle
// from unpkg the first time it mounts, then polls DexScreener every 30s.
// Falls back to a synthetic line series when no candle history is available.
const LWC_SRC = "https://unpkg.com/lightweight-charts/dist/lightweight-charts.standalone.production.js";
const TIMEFRAMES = [
  { id: "1H", label: "1H", minutes: 60 },
  { id: "4H", label: "4H", minutes: 240 },
  { id: "1D", label: "1D", minutes: 1440 },
];

function loadLwc() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.LightweightCharts) return Promise.resolve(window.LightweightCharts);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${LWC_SRC}"]`);
    if (existing) {
      // Script tag already in DOM — poll for the global until it appears.
      const start = Date.now();
      const tick = () => {
        if (window.LightweightCharts) return resolve(window.LightweightCharts);
        if (Date.now() - start > 8000) return reject(new Error("lwc timeout"));
        setTimeout(tick, 80);
      };
      tick();
      return;
    }
    const s = document.createElement("script");
    s.src = LWC_SRC;
    s.async = true;
    s.onload = () => resolve(window.LightweightCharts);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

const RblPriceChart = ({ contract }) => {
  const elRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const seriesKindRef = useRef("candles"); // 'candles' | 'line'
  const historyRef = useRef([]); // [{time, value/open/high/low/close}]
  const [tf, setTf] = useState("1H");
  const [stats, setStats] = useState({ price: null, change24h: null, dex: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Mount lightweight-charts once
  useEffect(() => {
    let alive = true;
    let resizeObs;
    let chart = null;
    loadLwc()
      .then((LWC) => {
        if (!alive || !LWC || !elRef.current) return;
        chart = LWC.createChart(elRef.current, {
          layout: { background: { color: "transparent" }, textColor: "#cbd5e1" },
          grid: {
            vertLines: { color: "rgba(255,255,255,0.04)" },
            horzLines: { color: "rgba(255,255,255,0.04)" },
          },
          rightPriceScale: { borderColor: "rgba(255,255,255,0.08)" },
          timeScale: { borderColor: "rgba(255,255,255,0.08)", timeVisible: true, secondsVisible: false },
          crosshair: { mode: 0 },
          autoSize: false,
          width: elRef.current.clientWidth,
          height: 320,
        });
        const series = chart.addCandlestickSeries({
          upColor: "#10b981", downColor: "#ef4444",
          borderUpColor: "#10b981", borderDownColor: "#ef4444",
          wickUpColor: "#10b981", wickDownColor: "#ef4444",
        });
        // Defer ref writes out of the effect's synchronous frame.
        queueMicrotask(() => {
          if (!alive) return;
          chartRef.current = chart;
          seriesRef.current = series;
          seriesKindRef.current = "candles";
          setErr(null);
        });
        resizeObs = new ResizeObserver(() => {
          if (elRef.current && chart) {
            chart.applyOptions({ width: elRef.current.clientWidth });
          }
        });
        resizeObs.observe(elRef.current);
      })
      .catch(() => { if (alive) setErr("chart_load_failed"); });
    return () => {
      alive = false;
      try { resizeObs?.disconnect?.(); } catch (_e) { /* noop */ }
      try { chart?.remove?.(); } catch (_e) { /* noop */ }
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // Poll DexScreener every 30s
  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const r = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${contract}`);
        const d = await r.json();
        const pair = (d?.pairs || []).sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];
        if (!mounted || !pair) { if (mounted) setLoading(false); return; }
        const price = parseFloat(pair.priceUsd || 0);
        const change24h = parseFloat(pair.priceChange?.h24 || 0);
        setStats({ price, change24h, dex: pair.dexId });

        // DexScreener does NOT expose OHLC arrays via this endpoint — only
        // priceUsd + bucket deltas (m5,h1,h6,h24). On first poll we synthesize
        // a 4-point history from those deltas so the chart isn't empty.
        const now = Math.floor(Date.now() / 1000);
        if (historyRef.current.length === 0) {
          const back = (deltaPct, secondsAgo) => {
            if (deltaPct == null || Number.isNaN(deltaPct)) return null;
            const p = price / (1 + deltaPct / 100);
            return Number.isFinite(p) && p > 0 ? { t: now - secondsAgo, p } : null;
          };
          const seed = [
            back(parseFloat(pair.priceChange?.h24), 86400),
            back(parseFloat(pair.priceChange?.h6), 6 * 3600),
            back(parseFloat(pair.priceChange?.h1), 3600),
            back(parseFloat(pair.priceChange?.m5), 5 * 60),
          ].filter(Boolean);
          historyRef.current.push(...seed);
        }
        historyRef.current.push({ t: now, p: price });
        // Trim to last 24h worth of ticks to bound memory.
        const cutoff = now - 86400;
        historyRef.current = historyRef.current.filter((x) => x.t >= cutoff);

        renderChart();
        setLoading(false);
      } catch (e) {
        if (mounted) { setErr("fetch_failed"); setLoading(false); }
      }
    };
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [contract, tf]);

  const renderChart = () => {
    if (!seriesRef.current || !chartRef.current) return;
    const LWC = window.LightweightCharts;
    if (!LWC) return;
    const tfMin = (TIMEFRAMES.find((x) => x.id === tf) || TIMEFRAMES[0]).minutes;
    const bucket = tfMin * 60;
    const ticks = historyRef.current;

    if (ticks.length < 2) {
      seriesRef.current.setData([]);
      return;
    }

    // Aggregate ticks into OHLC candles by bucket.
    const groups = new Map();
    for (const { t, p } of ticks) {
      const bs = Math.floor(t / bucket) * bucket;
      const g = groups.get(bs);
      if (!g) groups.set(bs, { time: bs, open: p, high: p, low: p, close: p });
      else { g.high = Math.max(g.high, p); g.low = Math.min(g.low, p); g.close = p; }
    }
    const candles = Array.from(groups.values()).sort((a, b) => a.time - b.time);

    // If we have at least 2 candles → render candles. Otherwise fall back to
    // a thin line series so users still see SOMETHING during the 30s window.
    if (candles.length >= 2) {
      if (seriesKindRef.current !== "candles") {
        try { chartRef.current.removeSeries(seriesRef.current); } catch (_e) { /* noop */ }
        seriesRef.current = chartRef.current.addCandlestickSeries({
          upColor: "#10b981", downColor: "#ef4444",
          borderUpColor: "#10b981", borderDownColor: "#ef4444",
          wickUpColor: "#10b981", wickDownColor: "#ef4444",
        });
        seriesKindRef.current = "candles";
      }
      seriesRef.current.setData(candles);
    } else {
      if (seriesKindRef.current !== "line") {
        try { chartRef.current.removeSeries(seriesRef.current); } catch (_e) { /* noop */ }
        seriesRef.current = chartRef.current.addLineSeries({ color: "#f59e0b", lineWidth: 2 });
        seriesKindRef.current = "line";
      }
      seriesRef.current.setData(ticks.map((x) => ({ time: x.t, value: x.p })));
    }
    chartRef.current.timeScale().fitContent();
  };

  // The fetchData effect re-runs when `tf` changes (via dep), so the chart
  // re-renders with the new bucket size automatically — no extra effect.

  const pos = (stats.change24h || 0) >= 0;
  const fmt = (v) => v == null ? "—" : (v < 0.01 ? v.toFixed(8) : v.toFixed(4));

  return (
    <section
      data-testid="tokens-chart-card"
      className="mt-6 rounded-2xl border border-white/10 bg-zinc-950/40 p-5 backdrop-blur"
    >
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-5 w-5 text-amber-300" />
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">R₿L / USD · Base L2</p>
            <p className="mt-0.5 font-mono">
              <span data-testid="chart-price" className="text-2xl font-black text-white">${fmt(stats.price)}</span>
              {stats.change24h != null && (
                <span
                  data-testid="chart-change-24h"
                  className={`ml-2 text-xs font-bold ${pos ? "text-emerald-300" : "text-rose-300"}`}
                >
                  {pos ? "+" : ""}{stats.change24h.toFixed(2)}% 24h
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-1" data-testid="chart-timeframe-selector">
          {TIMEFRAMES.map((t) => (
            <button
              key={t.id}
              onClick={() => setTf(t.id)}
              data-testid={`chart-tf-${t.id}`}
              className={`rounded-md border px-3 py-1 text-[10px] font-black uppercase tracking-wider transition-colors ${
                tf === t.id
                  ? "border-amber-400/60 bg-amber-400/10 text-amber-200"
                  : "border-white/10 bg-white/[0.03] text-white/55 hover:border-white/30 hover:text-white/80"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <div ref={elRef} className="relative h-[320px] w-full" data-testid="chart-canvas">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-white/40">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading R₿L data…
          </div>
        )}
      </div>
      <p className="mt-2 text-[10px] font-mono text-white/30">
        Data via DexScreener · refreshes every 30s · {stats.dex ? stats.dex.toUpperCase() : "—"}
      </p>
    </section>
  );
};

const StatPill = ({ label, value, extra, extraColor }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3 backdrop-blur">
    <p className="text-[9px] font-mono uppercase tracking-[0.22em] text-white/45">{label}</p>
    <p className="mt-1 font-mono text-lg font-black text-white">{value}</p>
    {extra && <p className={`mt-0.5 font-mono text-[10px] ${extraColor || "text-white/55"}`}>{extra}</p>}
  </div>
);

const Phase = ({ status, icon, title, items }) => {
  const palette = {
    done: { border: "border-emerald-400/40", bg: "bg-emerald-400/[0.04]", title: "text-emerald-200", check: <CheckCircle2 className="h-3 w-3 text-emerald-400" /> },
    active: {
      border: "border-cyan-400/40",
      bg: "bg-cyan-400/[0.04]",
      title: "text-cyan-200",
      // Filled dot with pulsing halo — clearly distinct from "upcoming"
      // empty circles. Reads as "in progress".
      check: (
        <span className="relative inline-flex h-3 w-3 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400/40" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
        </span>
      ),
    },
    upcoming: { border: "border-white/10", bg: "bg-white/[0.02]", title: "text-white/65", check: <Circle className="h-3 w-3 text-white/25" /> },
  }[status] || {};
  return (
    <div className={`rounded-2xl border ${palette.border} ${palette.bg} p-5 backdrop-blur`} data-testid={`phase-${status}`}>
      <h3 className={`text-sm font-black ${palette.title}`}>
        <span className="mr-2">{icon}</span>{title}
      </h3>
      <ul className="mt-3 space-y-1.5">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-[12px] text-white/70">
            <span className="mt-0.5 shrink-0">{palette.check}</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};


// ---------------------------------------------------------------------------
// StakingSection — 30-day R₿L stake with ⚡ ×1.5 multiplier badge.
// Self-contained: fetches its own data from /api/shadow/tokens/stakes.
// ---------------------------------------------------------------------------
const StakingSection = ({ balance, onMutated, lang, token }) => {
  const [data, setData] = useState({ stakes: [], summary: { has_active_stake: false, total_active_rbl: 0, multiplier: 1.0, lockup_days: 30, min_stake: 100 } });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/shadow/tokens/stakes`, { credentials: "include", headers });
      const j = await safeJson(r);
      if (r.ok && j) setData(j);
    } catch (_e) {
      // Network/CORS failure — staking is non-critical; UI shows empty state.
      console.warn("[ShadowTokens] stakes fetch failed:", _e?.message || _e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const t = (k) => ({
    es: { title: "Staking R₿L · 30 días", sub: "Bloquea R₿L durante 30 días y activa el multiplicador ⚡ ×1.5 sobre tus ganancias.", available: "Disponible para stakear", stake_btn: "Stakear 30 días", min: `Mín. ${data.summary.min_stake} R₿L`, active_title: "En staking activo", unlocks: "Desbloqueo", withdraw: "Retirar", locked: "Bloqueado", insufficient: "Saldo insuficiente", processing: "Procesando…", badge: "⚡ STAKER · ×1.5", multiplier_active: "Multiplicador activo" },
    en: { title: "R₿L Staking · 30 days", sub: "Lock R₿L for 30 days and activate the ⚡ ×1.5 multiplier on your earnings.", available: "Available to stake", stake_btn: "Stake 30 days", min: `Min. ${data.summary.min_stake} R₿L`, active_title: "Active stakes", unlocks: "Unlocks", withdraw: "Withdraw", locked: "Locked", insufficient: "Insufficient balance", processing: "Processing…", badge: "⚡ STAKER · ×1.5", multiplier_active: "Active multiplier" },
    sk: { title: "Staking R₿L · 30 dní", sub: "Zamkni R₿L na 30 dní a aktivuj multiplikátor ⚡ ×1.5 na zárobky.", available: "K dispozícii na staking", stake_btn: "Stakeovať 30 dní", min: `Min. ${data.summary.min_stake} R₿L`, active_title: "Aktívne staky", unlocks: "Odomknutie", withdraw: "Vybrať", locked: "Zamknuté", insufficient: "Nedostatok prostriedkov", processing: "Spracovanie…", badge: "⚡ STAKER · ×1.5", multiplier_active: "Aktívny multiplikátor" }, de: { title: "Staking R₿L · 30 Tage", sub: "Sperre R₿L für 30 Tage und aktiviere den Multiplikator ⚡ ×1.5 auf deine Gewinne.", available: "Verfügbar zum Staken", stake_btn: "30 Tage staken", min: `Min. ${data.summary.min_stake} R₿L`, active_title: "Im aktiven Staking", unlocks: "Entsperrung", withdraw: "Abheben", locked: "Gesperrt", insufficient: "Unzureichendes Guthaben", processing: "Verarbeitung…", badge: "⚡ STAKER · ×1.5", multiplier_active: "Multiplikator aktiv" }, "zh-CN": { title: "质押 R₿L · 30 天", sub: "锁定 R₿L 30 天并激活收益倍数 ⚡ ×1.5。", available: "可质押", stake_btn: "质押 30 天", min: `最低 ${data.summary.min_stake} R₿L`, active_title: "活跃质押中", unlocks: "解锁", withdraw: "提取", locked: "已锁定", insufficient: "余额不足", processing: "处理中…", badge: "⚡ STAKER · ×1.5", multiplier_active: "倍数已激活" },
  }[lang] || {})[k];

  const requested = parseInt(amount || 0, 10) || 0;
  const canStake = requested >= data.summary.min_stake && requested <= balance && !busy;

  const stake = async () => {
    if (!canStake) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/shadow/tokens/stake`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ amount: requested }),
      });
      const j = await safeJson(r);
      if (!r.ok) { setError((j?.detail?.error) || j?.detail || "stake_failed"); return; }
      setAmount("");
      await load();
      onMutated?.();
    } finally { setBusy(false); }
  };

  const unstake = async (stakeId) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/shadow/tokens/unstake`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ stake_id: stakeId }),
      });
      const j = await safeJson(r);
      if (!r.ok) { setError((j?.detail?.error) || j?.detail || "unstake_failed"); return; }
      await load();
      onMutated?.();
    } finally { setBusy(false); }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso.slice(0, 10); }
  };

  const active = (data.stakes || []).filter((s) => s.status === "active");

  return (
    <div className="mt-8" data-testid="tokens-staking-section">
      <div className="rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-500/[0.07] via-fuchsia-500/[0.03] to-transparent p-5 backdrop-blur">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-violet-200/80">// STAKING R₿L</p>
            <h3 className="mt-1 text-xl font-black tracking-tight text-white">{t("title")}</h3>
            <p className="mt-1 max-w-xl text-xs text-white/55">{t("sub")}</p>
          </div>
          {data.summary.has_active_stake && (
            <span
              data-testid="tokens-staker-badge"
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-violet-400/50 bg-violet-400/15 px-3 py-1 font-mono text-[10px] font-black uppercase tracking-[0.15em] text-violet-200 shadow-[0_0_18px_rgba(168,85,247,0.35)]"
            >
              {t("badge")}
            </span>
          )}
        </div>

        {/* Stake input */}
        <div className="mt-4 rounded-xl border border-white/10 bg-black/40 p-3">
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">
            <span>{t("available")}</span>
            <span className="text-white/75">{(balance || 0).toLocaleString()} R₿L</span>
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={data.summary.min_stake}
              max={balance}
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder={t("min")}
              data-testid="staking-amount-input"
              className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-sm outline-none focus:border-violet-400/50"
            />
            <button
              onClick={stake}
              disabled={!canStake}
              data-testid="staking-stake-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-[0_6px_20px_rgba(168,85,247,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(168,85,247,0.5)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? t("processing") : t("stake_btn")}
            </button>
          </div>
          {requested > 0 && requested > balance && (
            <p className="mt-1.5 text-[10px] text-rose-300">{t("insufficient")}</p>
          )}
          {error && (
            <p className="mt-1.5 text-[10px] text-rose-300" data-testid="staking-error">{String(error)}</p>
          )}
        </div>

        {/* Active stakes list */}
        {active.length > 0 && (
          <div className="mt-4" data-testid="staking-active-list">
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-violet-200/75">{t("active_title")}</p>
            <div className="mt-2 space-y-2">
              {active.map((s) => {
                const unlocked = s.unlock_at && new Date(s.unlock_at) <= new Date();
                return (
                  <div
                    key={s.id}
                    data-testid={`staking-row-${s.id}`}
                    className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.025] px-3 py-2"
                  >
                    <div>
                      <p className="font-mono text-base font-black text-violet-100">
                        {s.amount_rbl.toLocaleString()} <span className="text-[11px] text-violet-300">R₿L</span>
                        <span className="ml-2 inline-flex items-center rounded-full bg-violet-400/15 px-1.5 py-0.5 text-[9px] font-bold text-violet-200">⚡ ×{s.multiplier}</span>
                      </p>
                      <p className="mt-0.5 font-mono text-[10px] text-white/45">
                        {t("unlocks")}: <span className={unlocked ? "text-emerald-300" : "text-white/65"}>{fmt(s.unlock_at)}</span>
                      </p>
                    </div>
                    {unlocked ? (
                      <button
                        onClick={() => unstake(s.id)}
                        disabled={busy}
                        data-testid={`staking-unstake-${s.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/[0.08] px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-emerald-200 hover:border-emerald-400/70 hover:bg-emerald-400/[0.14] disabled:opacity-50"
                      >
                        {busy ? t("processing") : t("withdraw")}
                      </button>
                    ) : (
                      <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-white/45">🔒 {t("locked")}</span>
                    )}
                  </div>
                );
              })}
            </div>
            {data.summary.has_active_stake && (
              <p className="mt-2 text-[10px] font-mono text-violet-300/70">
                ⚡ {t("multiplier_active")}: ×{data.summary.multiplier}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
